import React, { useCallback, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { useTutorial } from '../../../components/tutorial';
import { authAPI } from '../../../services/api';
import { showDialog } from '../../../utils/dialogs';
import { profileWebStyles } from '../profileStyles';
import type { Translate } from '../profileTypes';
import { ProfileActionRow } from './ProfileActionRow';

interface CoachingProfilePanelProps {
  language: string;
  /** Stacked in a single column (phone/tablet). */
  isCompact: boolean;
  t: Translate;
}

/** "Coaching profile" panel: regenerate the intake profile, replay the tutorial. */
export function CoachingProfilePanel({ language, isCompact, t }: CoachingProfilePanelProps) {
  const { startTour } = useTutorial();
  const [regeneratingProfile, setRegeneratingProfile] = useState(false);

  // Replay the guided tour (QA F4). It opens each step's screen itself, so it
  // starts right here; `tour_replayed` is tracked by the provider.
  const handleReplayTutorial = useCallback(() => {
    startTour('profile');
  }, [startTour]);

  return (
    <View style={[profileWebStyles.panel, isCompact && profileWebStyles.panelStacked]}>
      <Text style={profileWebStyles.panelTitle}>{t('profile.webCoachingProfileTitle')}</Text>
      <Text style={profileWebStyles.panelCopy}>
        {t('profile.webCoachingProfileCopy')}
      </Text>
      <TouchableOpacity
        style={[profileWebStyles.primaryButton, regeneratingProfile && profileWebStyles.disabledButton]}
        disabled={regeneratingProfile}
        onPress={async () => {
          if (regeneratingProfile) return;
          try {
            setRegeneratingProfile(true);
            const lang: 'en' | 'tr' = language === 'tr' ? 'tr' : 'en';
            await authAPI.regenerateIntake(lang);
            const title = t('profile.regenSuccessTitle');
            const msg = t('profile.regenSuccessMessage');
            showDialog(title, msg);
          } catch (err: any) {
            const title = t('profile.regenFailedTitle');
            const msg = err?.response?.data?.detail || t('profile.regenFailedMessage');
            showDialog(title, msg);
          } finally {
            setRegeneratingProfile(false);
          }
        }}
        accessibilityRole="button"
      >
        {regeneratingProfile ? (
          <ActivityIndicator color="#FFFFFF" size="small" />
        ) : (
          <MaterialIcons name="auto-awesome" size={18} color="#FFFFFF" />
        )}
        <Text className="text-white" style={profileWebStyles.primaryButtonText}>
          {regeneratingProfile ? t('profile.regenerating') : t('profile.webRegenerateProfile')}
        </Text>
      </TouchableOpacity>
      <ProfileActionRow
        title={t('profile.webReplayTraining')}
        subtitle={t('profile.webReplayTrainingSubtitle')}
        icon="play-circle-outline"
        onPress={handleReplayTutorial}
        testID="profile_replay_tour"
      />
    </View>
  );
}
